import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowUp, Mic, Paperclip, Square } from 'lucide-react'
import { cn } from '../../lib/utils'
import { Button } from '../../components/button'
import { Kbd } from '../../components/kbd'
import { AgentBadge } from './agent-badge'
import { AgentSwitchPopover } from './agent-switch-popover'
import { AttachmentStrip } from './attachment-strip'
import { useDictation } from './hooks/use-dictation'
import { usePasteAttach } from './hooks/use-paste-attach'
import type { AgentDescriptor, AgentKey, Attachment, ChatStatus } from './types'

/**
 * Dictation: hold a key (or tap the mic) to record, release to have the host
 * turn the audio into text, which lands in the box for the person to read
 * and send. The browser cannot see the Fn key, so the default hold key is
 * Alt (Option on a Mac), which types nothing on its own.
 */
export interface ComposerDictation {
  /** Turn a recording into text. The Blob carries its real mime type. */
  transcribe: (audio: Blob) => Promise<string>
  /** `KeyboardEvent.key` to hold. Default 'Alt'. */
  holdKey?: string
  /** Send as soon as the text lands, instead of leaving it in the box. Default false. */
  sendOnRelease?: boolean
}

interface ComposerProps {
  agent: AgentDescriptor
  agents: AgentDescriptor[]
  status: ChatStatus
  attachments: Attachment[]
  onSend: (text: string) => void
  onStop: () => void
  onAttach: (files: File[]) => void
  /** Override for the paperclip click. When set, called instead
   * of opening the system file picker — host can wire into its
   * own asset gallery. Paste + drag-drop still go through
   * `onAttach`. */
  onPickAttachment?: () => void
  onRemoveAttachment: (id: string) => void
  onSelectAgent: (key: AgentKey) => void
  placeholder?: string
  /** Disable typing and sending, e.g. while the host's transport is
   * reconnecting. The placeholder is the place to say why. */
  disabled?: boolean
  /** Hide the attach control (and ignore paste-to-attach) for hosts whose
   * transport carries no attachments yet. */
  hideAttachments?: boolean
  /** Whether a streaming reply can be interrupted. Default true. When false
   * the send button stays put (disabled) during streaming instead of
   * turning into a stop button the host cannot honour. */
  canStop?: boolean
  /** Hold-to-talk dictation; the mic and the hold key appear only when set. */
  dictation?: ComposerDictation
  className?: string
}

/**
 * Composer surface: agent badge (anchors switch popover), attachment
 * strip, textarea, attach + send/stop. Send commits on Enter (Shift+
 * Enter inserts a newline). Empty messages are ignored. While
 * streaming, the send button becomes a stop button.
 */
export function Composer({
  agent,
  agents,
  status,
  attachments,
  onSend,
  onStop,
  onAttach,
  onPickAttachment,
  onRemoveAttachment,
  onSelectAgent,
  placeholder,
  disabled = false,
  hideAttachments = false,
  canStop = true,
  dictation,
  className,
}: ComposerProps) {
  const [draft, setDraft] = useState('')
  const [switchOpen, setSwitchOpen] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const draftRef = useRef(draft)
  draftRef.current = draft

  const isStreaming = status === 'streaming' || status === 'submitting'
  const canSend =
    !disabled &&
    !isStreaming &&
    (draft.trim().length > 0 ||
      attachments.some((a) => a.uploadStatus !== 'uploading'))

  // ── Dictation ────────────────────────────────────────────────────────────
  const dictate = useDictation()
  const dictationOn = !!dictation && dictate.supported
  const listening = dictate.state === 'starting' || dictate.state === 'recording'
  const transcribing = dictate.state === 'transcribing'
  const holdKey = dictation?.holdKey ?? 'Alt'
  const sendOnReleaseRef = useRef(false)
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    if (!dictate.startedAt) {
      setElapsed(0)
      return
    }
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - (dictate.startedAt ?? 0)) / 1000)), 250)
    return () => clearInterval(t)
  }, [dictate.startedAt])

  const finishDictation = useCallback(async () => {
    if (!dictation) return
    const blob = await dictate.stop()
    if (!blob || blob.size < 1200) {
      // Under a tenth of a second of audio: a tap, not a sentence.
      dictate.setState('idle')
      return
    }
    dictate.setState('transcribing')
    try {
      const text = (await dictation.transcribe(blob)).trim()
      dictate.setState('idle')
      if (!text) return
      const before = draftRef.current
      const joined = before.trim() ? `${before.replace(/\s+$/, '')} ${text}` : text
      setDraft(joined)
      if (dictation.sendOnRelease || sendOnReleaseRef.current) {
        sendOnReleaseRef.current = false
        if (!disabled && !isStreaming) {
          onSend(joined.trim())
          setDraft('')
          return
        }
      }
      requestAnimationFrame(() => {
        const ta = textareaRef.current
        if (!ta) return
        ta.focus()
        ta.setSelectionRange(joined.length, joined.length)
      })
    } catch (err) {
      dictate.setState('error')
      dictate.setError(err instanceof Error ? err.message : 'Could not transcribe that.')
    }
  }, [dictation, dictate, disabled, isStreaming, onSend])

  // Hold the key anywhere in the page while nothing else is being typed with
  // it: keydown starts, keyup finishes. `repeat` filters the auto-repeat.
  useEffect(() => {
    if (!dictationOn || disabled) return
    const down = (e: KeyboardEvent) => {
      if (e.key !== holdKey || e.repeat) return
      if (dictate.state !== 'idle' && dictate.state !== 'error') return
      void dictate.start()
    }
    const up = (e: KeyboardEvent) => {
      if (e.key !== holdKey) return
      if (dictate.state === 'starting' || dictate.state === 'recording') void finishDictation()
    }
    window.addEventListener('keydown', down)
    window.addEventListener('keyup', up)
    return () => {
      window.removeEventListener('keydown', down)
      window.removeEventListener('keyup', up)
    }
  }, [dictationOn, disabled, holdKey, dictate, finishDictation])

  const toggleMic = () => {
    if (listening) void finishDictation()
    else if (dictate.state === 'idle' || dictate.state === 'error') void dictate.start()
  }

  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform)
  const holdKeyLabel = holdKey === 'Alt' ? (isMac ? 'Option' : 'Alt') : holdKey
  /** What the badge on the mic shows: the Mac glyph, or the key's name. */
  const holdKeyGlyph = holdKey === 'Alt' ? (isMac ? '⌥' : 'Alt') : holdKey === 'Control' ? (isMac ? '⌃' : 'Ctrl') : holdKey === 'Shift' ? '⇧' : holdKey

  const ignoreAttach = useCallback(() => {}, [])
  usePasteAttach(textareaRef.current, hideAttachments ? ignoreAttach : onAttach)

  useEffect(() => {
    const ta = textareaRef.current
    if (!ta) return
    ta.style.height = '0px'
    const next = Math.min(ta.scrollHeight, 200)
    ta.style.height = `${next}px`
  }, [draft])

  const commit = () => {
    if (!canSend) return
    const text = draft.trim()
    if (!text && attachments.length === 0) return
    onSend(text)
    setDraft('')
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      commit()
    }
  }

  return (
    <div
      data-slot="agent-chat-composer"
      className={cn(
        'relative flex flex-col gap-2 rounded-2xl border border-black/[0.06] bg-background p-2',
        'dark:border-white/[0.06]',
        'focus-within:border-black/10 dark:focus-within:border-white/10',
        className
      )}
    >
      {/* File input lives OUTSIDE the action row so its
          position-absolute (sr-only) hit-area can't end up in
          the row's flex flow on any browser. Clicked
          programmatically by the paperclip button. */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*"
        className="sr-only"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? [])
          if (files.length > 0) onAttach(files)
          e.currentTarget.value = ''
        }}
      />
      {attachments.length > 0 && (
        <AttachmentStrip
          attachments={attachments}
          onRemove={onRemoveAttachment}
          className="px-1"
        />
      )}
      {dictationOn && (listening || transcribing || dictate.state === 'error') && (
        <div
          className={cn(
            'flex items-center gap-2 px-2 text-xs',
            dictate.state === 'error' ? 'text-destructive' : 'text-muted-foreground'
          )}
          role="status"
          aria-live="polite"
        >
          {listening && (
            <>
              <span className="relative flex size-2">
                <span className="absolute inline-flex size-full animate-ping rounded-full bg-destructive/60" />
                <span className="relative inline-flex size-2 rounded-full bg-destructive" />
              </span>
              <span>Listening… release {holdKeyLabel} or tap the mic to finish</span>
              <span className="ml-auto tabular-nums">{Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')}</span>
            </>
          )}
          {transcribing && <span>Writing that down…</span>}
          {dictate.state === 'error' && <span>{dictate.error ?? 'Dictation failed.'}</span>}
        </div>
      )}
      <textarea
        ref={textareaRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder ?? (dictationOn ? `Message ${agent.label}… or hold ${holdKeyLabel} to talk` : `Message ${agent.label}…`)}
        rows={1}
        disabled={disabled}
        className={cn(
          'w-full min-h-[4rem] max-h-[12.5rem] resize-none bg-transparent px-2 py-1 text-sm leading-relaxed outline-none placeholder:text-muted-foreground'
        )}
        aria-label={`Message ${agent.label}`}
      />
      <div className="flex items-center gap-1.5 px-1">
        {agents.length > 1 ? (
          <AgentSwitchPopover
            agents={agents}
            activeKey={agent.key}
            activeAgent={agent}
            onSelect={onSelectAgent}
            open={switchOpen}
            onOpenChange={setSwitchOpen}
          />
        ) : (
          // One agent: the badge still names who you are talking to, but it
          // is inert. A switcher that opens a one-item list is a dead end.
          <AgentBadge
            agent={agent}
            showCaret={false}
            aria-haspopup={undefined}
            tabIndex={-1}
            className="pointer-events-none"
          />
        )}
        {!hideAttachments && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => {
              if (onPickAttachment) onPickAttachment()
              else fileInputRef.current?.click()
            }}
            aria-label="Attach files"
          >
            <Paperclip />
          </Button>
        )}
        {dictationOn && (
          // The mic as a labelled button that teaches its own shortcut: the key's
          // glyph in a kbd chip and its name beside it (⌥ Option on a Mac, Alt
          // elsewhere). While recording it turns red and says so.
          <Button
            type="button"
            variant={listening ? 'destructive' : 'ghost'}
            size="sm"
            leftIcon={<Mic />}
            onClick={toggleMic}
            disabled={disabled || transcribing}
            aria-pressed={listening}
            aria-label={listening ? 'Stop dictating' : `Dictate: tap, or hold ${holdKeyLabel}`}
            title={listening ? 'Stop dictating' : `Dictate: tap, or hold ${holdKeyLabel}`}
            className="gap-1.5"
          >
            {listening ? (
              'Listening…'
            ) : transcribing ? (
              'Writing…'
            ) : (
              <>
                <Kbd aria-hidden className="h-4 min-w-4 px-1 text-[10px] leading-4">
                  {holdKeyGlyph}
                </Kbd>
                {holdKeyGlyph !== holdKeyLabel ? <span className="text-xs">{holdKeyLabel}</span> : null}
              </>
            )}
          </Button>
        )}
        <div className="flex-1" />
        {isStreaming && canStop ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={onStop}
            aria-label="Stop streaming"
            className="relative z-[1]"
          >
            <Square />
          </Button>
        ) : (
          <Button
            type="button"
            variant="primary"
            size="icon-sm"
            disabled={!canSend}
            onClick={commit}
            aria-label="Send"
            className="relative z-[1]"
          >
            <ArrowUp />
          </Button>
        )}
      </div>
    </div>
  )
}
